/*! Copyright (c) Safe As Milk. All rights reserved. */
import Cart from "cart-store";

/* Hyspecs (2026-07-18): the cart carousel only suggests whole pairs and
 * spare lenses — never Rx frames/lenses (type LensAdvizor), side shields
 * (Parts + "Side Shields" tag), accessories/apparel/gift cards (empty or
 * other types). Recommendation-API products carry `type` + `tags`.
 * 2026-10-22: Parts must carry the "Spares" tag (every spare lens does), so
 * a future non-lens part can't slip in. */
const hysCarouselAllowed = (product) => {
    const tags = product.tags || [];
    return (product.type === "Whole Pair" || product.type === "Sunglasses" || (product.type === "Parts" && tags.includes("Spares") && !tags.includes("Side Shields"))) && hysCanAddFromTile(product);
};

/* Uploaded file name of an image (string URL or {src}/{url} object), so
 * "//cdn.shopify.com/..." and "/cdn/shop/files/..." forms compare equal. */
const hysImageFile = (img) => {
    const src = typeof img === "string" ? img : img && (img.src || img.url);
    return src ? src.split("?")[0].split("/").pop() : null;
};

/* Colours the tile picker offers: in stock, CSA + ANSI only. */
const hysPickerVariants = (product) => (product.variants || []).filter((v => v.available && !/ANSI-Only/i.test(v.title || "")));

/* Hyspecs (2026-10-22, Peter): only suggest what the tile can add. A
 * multi-colour product with no CSA + ANSI colour in stock (ICON SLIM SIGMA 8K:
 * Barely Pink sold out, only ANSI-Only left) kept the theme's quick-shop
 * button, which closed the drawer and opened nothing. */
const hysCanAddFromTile = (product) => {
    if ((product.variants || []).length <= 1) return product.available !== false;
    return hysPickerVariants(product).length > 0;
};

/* Price a tile is ranked at: the cheapest colour the picker offers (in
 * stock, CSA + ANSI), else the first variant, else the product price. The
 * compact pool entries use the same rule, computed in Liquid. */
const hysTilePrice = (product) => {
    const prices = hysPickerVariants(product).map((v => v.price));
    if (prices.length) return Math.min(...prices);
    const first = (product.variants || [])[0];
    return first ? first.price : product.price;
};

/* Hyspecs (2026-10-22, Peter): "Complete your kit" exists to lift AOV and get
 * carts over the free-shipping minimum, so tiles are ranked instead of shown
 * in recommendation-API order. Each candidate scores:
 *   complement  +2 for the opposite lens use to the pairs in the cart
 *               (indoor <-> outdoor, the Indoor + Outdoor kit), +1 for a
 *               hybrid lens (5050, Adaptix) against a one-sided cart, +1 for
 *               everything when the cart is mixed or has no pairs; then for
 *               a pair in the same frame family as one in the cart, +2 for
 *               Revs and Flyers, +1 otherwise (order history: per product,
 *               2nd pairs stay in the frame 3.1x for Revs, 2.7x Flyers, 1.4x
 *               Icon, 1.3x Icon Slim). Spare lenses only show when their
 *               frame is in the cart, and get +1 for it.
 *   shipping    under the minimum only: +2 if adding it reaches free
 *               shipping, else up to +1 for how close it gets. Uses what the
 *               item ADDS to the cart total, after the extra-pair 20% off.
 *   offer       +0.5 when it would get the extra-pair 20% off.
 * Ties: under the minimum the cheaper one first (easiest way over the line),
 * at or over it the pricier one first (AOV). The pinned hardcase stays
 * first whatever the score. */
const HYS_PAIR_TYPES = [ "Whole Pair", "Sunglasses" ];

const HYS_STRONG_FRAME_FAMILIES = [ "revs", "flyers" ];

const hysFrameFamily = (title) => {
    const t = (title || "").toUpperCase();
    if (t.startsWith("ICON SLIM")) return "icon-slim";
    if (t.startsWith("ICON")) return "icon";
    if (t.startsWith("REVS")) return "revs";
    if (t.startsWith("FLYERS")) return "flyers";
    return null;
};

/* Titles are consistent where the indoor/outdoor tags are patchy. Order
 * matters: "GREY TINTED + NK ANTI-FOG" is a sun lens. */
const hysLensUse = (title) => {
    const t = (title || "").toUpperCase();
    if (/POLARI[SZ]ED|TINTED|SIGMA|GRADIENT|HEATSHIELD/.test(t)) return "outdoor";
    if (/5050|50\/50|ADAPTIX|PHOTOCHROMIC/.test(t)) return "hybrid";
    if (/CLEAR|ANTI-FOG|YELLOW/.test(t)) return "indoor";
    return null;
};

/* Cents the extra-pair offer takes off a set of pair unit prices: the
 * cheapest min(n - 1, maxExtra) at percent. Mirrors the three non-stacking
 * Buy X Get Y discounts (Shopify applies the best one, cheapest pairs). */
const hysExtraPairDiscount = (prices, offer) => {
    if (!offer || prices.length < 2) return 0;
    const cheapest = [ ...prices ].sort(((a, b) => a - b)).slice(0, Math.min(prices.length - 1, offer.maxExtra));
    return cheapest.reduce(((sum, p) => sum + p), 0) * offer.percent / 100;
};

const hysRankKit = (list, cart, extraPairOffer, minimum) => {
    /* Any discount code in the cart (e.g. a /discount/FALL30 link) can
     * replace the automatic extra-pair BXGY, since they don't combine, and a
     * code that isn't applicable yet can become applicable BECAUSE of the
     * suggested pair (FALL30 needs an NK Anti-Fog pair). The 20% model can't
     * predict that, so pairs rank at full price while any code is attached
     * (2026-10-23; it used to check only codes already applicable). */
    const offer = (cart.discount_codes || []).length ? null : extraPairOffer;
    const pairLines = (cart.items || []).filter((item => HYS_PAIR_TYPES.includes(item.product_type)));
    const uses = new Set(pairLines.map((item => hysLensUse(item.product_title))));
    const families = new Set(pairLines.map((item => hysFrameFamily(item.product_title))).filter(Boolean));
    const oneSided = uses.has("indoor") !== uses.has("outdoor");
    const opposite = uses.has("outdoor") ? "indoor" : "outdoor";
    const offerPrices = offer ? pairLines.filter((item => offer.ids.has(item.product_id))).flatMap((item => Array(item.quantity).fill(item.original_price))) : [];
    const discountNow = hysExtraPairDiscount(offerPrices, offer);
    const gap = minimum > 0 ? minimum - (cart.total_price || 0) : 0;
    return list.filter((({item: product}) => product.type !== "Parts" || families.has(hysFrameFamily(product.title)))).map((entry => {
        const product = entry.item;
        const price = hysTilePrice(product);
        const adds = offer && offer.ids.has(product.id) ? price - (hysExtraPairDiscount([ ...offerPrices, price ], offer) - discountNow) : price;
        const use = hysLensUse(product.title);
        let score = oneSided ? (use === opposite ? 2 : use === "hybrid" ? 1 : 0) : 1;
        const productFamily = hysFrameFamily(product.title);
        if (families.has(productFamily)) score += product.type !== "Parts" && HYS_STRONG_FRAME_FAMILIES.includes(productFamily) ? 2 : 1;
        if (gap > 0) score += adds >= gap ? 2 : Math.max(0, adds / gap);
        if (adds < price) score += .5;
        return {
            entry: entry,
            score: score,
            adds: adds
        };
    })).sort(((a, b) => b.score - a.score || (gap > 0 ? a.adds - b.adds : b.adds - a.adds) || a.entry.weight - b.entry.weight)).map((({entry: entry}) => entry));
};

class CartRecommendations extends HTMLElement {
    async connectedCallback() {
        this.sectionId = this.getAttribute("section-id");
        this.recommendationsAmount = 10;
        this.recommendations = [];
        this.sectionTitle = this.querySelector("cart-recommendations-title");
        this.container = this.querySelector("[cart-recommendations-content]");
        this.itemsScroll = this.querySelector("items-scroll");
        this.intent = this.getAttribute("product-recommendations-type");
        this.cards = Array.from(this.container.children);
        /* Kit ranking inputs (snippet's data-hys-kit-config): the extra-pair
         * offer (null when off or paused: pairs ranked at full price) and the
         * compact candidate pool. Full pool products load on demand into
         * poolProducts (id -> product.js JSON, or null when it failed).
         * 2026-10-23: poolProducts and poolLoading are kept together across
         * reconnects. Resetting only poolProducts left loaded handles marked
         * as loading, so they never came back for this element. */
        this.offer = null;
        this.pool = [];
        this.poolProducts = this.poolProducts || new Map;
        this.poolLoading = this.poolLoading || new Set;
        try {
            const configEl = this.querySelector("[data-hys-kit-config]");
            const config = configEl ? JSON.parse(configEl.textContent) : {};
            const ep = config.offer;
            if (ep && ep.on === true && Array.isArray(ep.productIds) && ep.productIds.length) {
                this.offer = {
                    percent: Number(ep.percent) || 20,
                    maxExtra: Number(ep.maxExtra) || 3,
                    ids: new Set(ep.productIds.map(Number))
                };
            }
            if (Array.isArray(config.pool)) this.pool = config.pool;
        } catch (e) {
            this.offer = null;
            this.pool = [];
        }
        /* Hyspecs (2026-07-18): pinned upsell product (the hardcase) rendered
         * as the ALWAYS-FIRST card instead of a standalone drawer block —
         * bypasses hysCarouselAllowed, and because prepareRecommendations
         * already drops in-cart products, it hides itself when added and
         * returns when removed. 2026-10-22: fetched in parallel (see
         * #loadPinnedProduct) instead of awaited before subscribing. The
         * await delayed the whole carousel, and an element removed during it
         * subscribed anyway once the fetch resolved (a zombie subscription
         * that disconnectedCallback had already missed). */
        this.pinnedProduct = null;
        const connection = this.hysConnection = (this.hysConnection || 0) + 1;
        const onCartState = ((state, prevState) => {
            /* Defensive: a malformed store (the old Cart.clear() wrote
             * cart = {} and recommendations = {}) must not throw here. */
            if (!state || !state.cart || !Array.isArray(state.cart.items)) return;
            const recommendationItems = state.recommendations && state.recommendations.items || {};
            const productVariantsBeingAdded = state.productVariantsBeingAdded || [];
            const getCartProductsWithoutBeingAdded = (items, productVariantsBeingAdded) => items.reduce(((ids, item) => {
                const id = item.product_id.toString();
                if (productVariantsBeingAdded.includes(item.variant_id.toString())) return ids;
                if (!ids.includes(id)) {
                    ids.push(id);
                }
                return ids;
            }), []);
            const finalCartProductsIds = getCartProductsWithoutBeingAdded(state.cart.items, productVariantsBeingAdded);
            /* rawCartItems: the pinned check must use the REAL cart contents —
             * cartProductsIds excludes variants still in productVariantsBeingAdded
             * (anti-flicker for ordinary recs), which never clears while the
             * drawer stays open, so the pinned card would survive its own add. */
            const prepareRecommendations = (recommendationItems, cartProductsIds, rawCartItems) => {
                let list = Object.values(recommendationItems).reduce(((recommendations, value) => {
                    const weightedRecommendations = value.products.reduce(((uniqueProducts, product, i) => {
                        if (hysCarouselAllowed(product) && !recommendations.find((r => r.item.id === product.id)) && !cartProductsIds.includes(product.id.toString())) {
                            uniqueProducts.push({
                                weight: (i + 1) / value.weight,
                                item: product
                            });
                        }
                        return uniqueProducts;
                    }), []);
                    return [ ...recommendations, ...weightedRecommendations ];
                }), []).sort(((a, b) => a.weight - b.weight));
                /* Pool products Shopify didn't return as related join the
                 * ranking, behind related ones on ties. Until its full JSON
                 * has loaded a pool item ranks on its compact entry; it can
                 * only be SHOWN once loaded (see #loadPoolProducts). */
                if ((rawCartItems || []).length) this.pool.forEach(((entry, i) => {
                    if (cartProductsIds.includes(String(entry.id)) || list.some((r => r.item.id === entry.id))) return;
                    const full = this.poolProducts.get(entry.id);
                    if (full === null || (full && !hysCarouselAllowed(full))) return;
                    list.push({
                        weight: 1e6 + i,
                        item: full || entry,
                        handle: entry.handle,
                        pending: !full
                    });
                }));
                list = hysRankKit(list, state.cart, this.offer, this.#freeShippingMinimum()).slice(0, this.recommendationsAmount);
                const pending = list.filter((r => r.pending));
                if (pending.length) this.#loadPoolProducts(pending.map((r => r.handle)));
                list = list.filter((r => !r.pending));
                /* Anti-flicker vs re-ranking (2026-10-23): once a pair lands
                 * in the cart the cart changes, the ranking moves, and the
                 * tile being added could drop out of the top 10 mid-add,
                 * taking its "Adding…" / tick with it. A rendered tile whose
                 * product is still pending keeps its place until its add
                 * settles (its caller, or the store's grace clear, resets
                 * the pending ids). */
                const pendingVariantIds = productVariantsBeingAdded.map(String);
                if (pendingVariantIds.length) {
                    const addingProductIds = new Set((rawCartItems || []).filter((item => pendingVariantIds.includes(String(item.variant_id)))).map((item => item.product_id)));
                    (this.recommendations || []).forEach(((entry, index) => {
                        if (addingProductIds.has(entry.item.id) && !list.some((r => r.item.id === entry.item.id))) list.splice(Math.min(index, list.length), 0, entry);
                    }));
                }
                const pinnedInCart = (rawCartItems || []).some((item => item.product_id === this.pinnedProduct?.id));
                list = list.filter((r => r.item.id !== this.pinnedProduct?.id || !pinnedInCart));
                if (this.pinnedProduct && this.pinnedProduct.available !== false && !pinnedInCart) {
                    list = list.filter((r => r.item.id !== this.pinnedProduct.id));
                    list.unshift({
                        weight: -1e9,
                        item: this.pinnedProduct
                    });
                }
                return list;
            };
            const newRecommendations = prepareRecommendations(recommendationItems, finalCartProductsIds, state.cart.items);
            /* Hyspecs (2026-07-19, Peter): dedupe against what was actually
             * RENDERED, not a recompute of the previous store state. The old
             * prev-vs-new compare skipped the very first render whenever the
             * recommendations API returned 0 usable products — both sides
             * reduced to [pinned hardcase], so the early-return fired before
             * the unhide and a 1-item cart showed no carousel at all. */
            const newRenderedIds = newRecommendations.map((({item: item}) => item.id)).toString();
            if (this.renderedIds === newRenderedIds) return;
            this.renderedIds = newRenderedIds;
            /* Cards are about to move: an open colour menu is fixed-position
             * and would stay where its tile used to be. */
            this.#closeMenus();
            this.recommendations = newRecommendations;
            this.cards = this.cards.reduce(((newItems, item) => {
                const cardProductId = item.getAttribute("product-id");
                const itemInRecommendations = this.recommendations.find((({item: i}) => i.id.toString() === cardProductId));
                if (!itemInRecommendations) {
                    item.remove();
                    return newItems;
                }
                newItems.push(item);
                return newItems;
            }), []);
            const currentCardsProductIds = this.cards.map((card => card.getAttribute("product-id")));
            const newCards = [];
            this.recommendations.forEach((({item: item}, i) => {
                if (!currentCardsProductIds.includes(item.id.toString())) {
                    const newCard = this.#createCard();
                    if (i === 0) {
                        this.container.prepend(newCard);
                    } else {
                        newCards[i - 1].after(newCard);
                    }
                    newCard.sectionId = this.sectionId;
                    newCard.product = item;
                    newCard.id = `cart-recommendation--${item.id}`;
                    newCards.push(newCard);
                } else {
                    const oldCard = this.cards.find((j => j.getAttribute("product-id") === item.id.toString()));
                    if (currentCardsProductIds.indexOf(oldCard.getAttribute("product-id")) !== i) {
                        if (i === 0) {
                            this.container.prepend(oldCard);
                        } else {
                            newCards[i - 1].after(oldCard);
                        }
                    }
                    newCards.push(oldCard);
                }
            }));
            this.cards = newCards;
            this.cards.forEach((card => this.#setupHysVariantPicker(card)));
            /* Tiles created while an add is in flight (e.g. pool products
             * that just loaded) start locked like the rest. */
            if (this.hysPendingAdds > 0) this.#setLocked(true);
            if (this.itemsScroll) this.itemsScroll.smoothScrollItems(0);
            if (this.recommendations.length > 0) {
                this.removeAttribute("hidden");
            } else {
                this.setAttribute("hidden", "");
            }
        });
        this.unsubscribe = Cart.subscribe(onCartState);
        /* Re-runs the render with the current store state once pool
         * products or the pinned product have loaded (no store change fires
         * for that). No-op once this element is gone. */
        this.rerender = () => {
            if (this.isConnected && connection === this.hysConnection) onCartState(Cart.getState());
        };
        /* One pair of page-level listeners per carousel (2026-10-22): each
         * picker card used to add its own and never removed them, so they
         * piled up as cards were recreated. Any scroll closes this
         * carousel's menus (the anchor would drift); a click outside a
         * picker closes its menu. */
        this.hysOnDocScroll = () => this.#closeMenus();
        this.hysOnDocClick = (event => {
            this.querySelectorAll(".hys-mini-picker").forEach((wrap => {
                if (!wrap.contains(event.target)) wrap.querySelector(".hys-mini-picker__menu").hidden = true;
            }));
        });
        document.addEventListener("scroll", this.hysOnDocScroll, {
            capture: true,
            passive: true
        });
        document.addEventListener("click", this.hysOnDocClick);
        /* Add lock (2026-10-23, post-hotfix review): while ANY cart add is in
         * flight (this carousel's picker or direct-add tiles, or the PDP
         * form), every tile control is really disabled and submits inside
         * the carousel are blocked. A CSS-only lock still let keyboard and
         * programmatic submits through, so two adds could overlap. The
         * Cart store announces each add with on:cart:loading and ends it
         * with on:cart:loaded or on:cart:failed. */
        this.hysPendingAdds = 0;
        this.hysOnCartLoading = (event => {
            if (!event.detail || event.detail.type !== "ADD") return;
            this.hysPendingAdds += 1;
            this.#setLocked(true);
        });
        this.hysOnCartDone = (event => {
            if (!event.detail || event.detail.type !== "ADD") return;
            this.hysPendingAdds = Math.max(0, this.hysPendingAdds - 1);
            if (!this.hysPendingAdds) this.#setLocked(false);
        });
        this.hysOnSubmit = (event => {
            if (this.hysPendingAdds > 0) {
                event.preventDefault();
                event.stopImmediatePropagation();
            }
        });
        document.addEventListener("on:cart:loading", this.hysOnCartLoading);
        document.addEventListener("on:cart:loaded", this.hysOnCartDone);
        document.addEventListener("on:cart:failed", this.hysOnCartDone);
        this.addEventListener("submit", this.hysOnSubmit, true);
        const pinnedHandle = this.getAttribute("pinned-product-handle");
        if (pinnedHandle) this.#loadPinnedProduct(pinnedHandle, connection);
        await Cart.enableCartRecommendations(this.intent);
    }
    async #loadPinnedProduct(handle, connection) {
        let product = null;
        try {
            const rootRoute = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : "/";
            const response = await fetch(`${rootRoute}products/${handle}.js`);
            if (response.ok) product = await response.json();
        } catch (e) {
            product = null;
        }
        if (connection !== this.hysConnection || !this.isConnected) return;
        this.pinnedProduct = product;
        this.rerender();
    }
    #closeMenus() {
        this.querySelectorAll(".hys-mini-picker__menu").forEach((menu => {
            menu.hidden = true;
        }));
    }
    /* Disable every enabled tile control (picker buttons and options,
     * direct-add submit buttons) and mark the ones this lock disabled, so
     * unlocking re-enables only those: the busy picker manages its own. */
    #setLocked(locked) {
        this.toggleAttribute("hys-adding", locked);
        this.querySelectorAll("product-card-button button").forEach((button => {
            if (locked) {
                if (!button.disabled) {
                    button.disabled = true;
                    button.dataset.hysLocked = "1";
                }
            } else if (button.dataset.hysLocked) {
                button.disabled = false;
                delete button.dataset.hysLocked;
            }
        }));
    }
    /* Hyspecs (2026-07-19, Peter): multi-variant tiles used to open the
     * full quick-shop modal. Instead, swap the trigger for an inline
     * popover listing the eligible frame colours — CSA+ANSI variants only
     * (always: options are labelled by colour alone, so ANSI-Only twins would
     * look identical; display choice, not the Crew Pack eligibility rule) —
     * one tap on a colour adds it straight to the cart via the Cart store.
     * Single-variant tiles keep their direct add form. Idempotent per card
     * (re-render loop reuses card elements). */
    #setupHysVariantPicker(card) {
        if (!card || card.dataset.hysPicker || !card.product) return;
        const trigger = card.querySelector("[data-trigger-quick-shop]");
        if (!trigger) return;
        const eligible = hysPickerVariants(card.product);
        if (!eligible.length) return;
        card.dataset.hysPicker = "1";
        /* The picker replaces the quick-shop trigger, so the card's
         * quick-shop modal is dead weight, and keeping it broke the vendor
         * card: on every DOM move (re-ranking moves cards a lot) its
         * connected/disconnected callbacks re-bound the missing trigger and
         * its items-scroll listener re-parented the modal, throwing "null
         * addEventListener" / "appendChild" errors (found 2026-10-22). With
         * card.quickShop null those code paths are skipped. */
        if (card.quickShop) {
            card.quickShop.remove();
            card.quickShop = null;
        }
        const shown = this.#showPickerColour(card, eligible);
        const wrap = document.createElement("span");
        wrap.className = "hys-mini-picker";
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = trigger.className;
        btn.textContent = trigger.textContent.trim();
        wrap.appendChild(btn);
        const menu = document.createElement("div");
        menu.className = "hys-mini-picker__menu";
        menu.hidden = true;
        eligible.forEach((variant => {
            const opt = document.createElement("button");
            opt.type = "button";
            opt.className = "hys-mini-picker__option";
            const parts = (variant.title || "").split("/");
            opt.textContent = parts[parts.length - 1].trim() || variant.title;
            /* Add lifecycle (2026-10-22, independent review): one add at a
             * time (the store-event lock in connectedCallback disables every
             * tile control meanwhile); the tile shows "Adding…" and stays
             * visible until done (string ids make the anti-flicker filter
             * keep it); a failure shows the reason under the button; pending
             * store state is cleared like product-form.js does. Cart.add only
             * rejects when the add itself failed (cart-store.js no longer
             * rejects on a recommendations error). */
            opt.addEventListener("click", (async () => {
                if (this.hysPendingAdds > 0) return;
                menu.hidden = true;
                this.#showPickerError(wrap, null);
                const label = btn.textContent;
                btn.disabled = true;
                btn.setAttribute("aria-busy", "true");
                btn.textContent = "Adding…";
                menu.querySelectorAll(".hys-mini-picker__option").forEach((o => {
                    o.disabled = true;
                }));
                let failure = null;
                try {
                    await Cart.add({
                        id: variant.id,
                        quantity: 1
                    });
                } catch (e) {
                    failure = e;
                } finally {
                    await Cart.resetLatestAddedProduct();
                    await Cart.resetVariantsBeingAdded(variant.id);
                }
                btn.disabled = false;
                btn.removeAttribute("aria-busy");
                btn.textContent = label;
                menu.querySelectorAll(".hys-mini-picker__option").forEach((o => {
                    o.disabled = false;
                }));
                if (failure) this.#showPickerError(wrap, failure.message || "Couldn't add this pair. Please try again.");
            }));
            menu.appendChild(opt);
        }));
        wrap.appendChild(menu);
        btn.addEventListener("click", (event => {
            event.stopPropagation();
            if (this.hysPendingAdds > 0) return;
            this.#showPickerError(wrap, null);
            /* stopPropagation keeps this tap from reaching the document
             * click listener, so another tile's open menu never heard it and
             * menus stacked up (Peter 2026-10-22). Close the others here. */
            document.querySelectorAll(".hys-mini-picker__menu").forEach((other => {
                if (other !== menu) other.hidden = true;
            }));
            /* One-click add is only safe when the colour we'd add is the
             * colour the tile pictures (Peter 2026-07-26): with the pictured
             * colourway sold out, the old unconditional shortcut silently
             * added the other one (ICON SLIM SIGMA 8K showed Barely Pink,
             * added Lilac). Same-image lone variant keeps the shortcut;
             * otherwise the menu opens so the choice is explicit. */
            if (eligible.length === 1 && (shown === eligible[0] || this.#variantMatchesTileImage(eligible[0], card.product))) {
                menu.querySelector(".hys-mini-picker__option").click();
                return;
            }
            if (menu.hidden) {
                /* position: fixed so the menu escapes the carousel's
                 * overflow clipping (the tile scroller cut it off). Anchored
                 * above the button, clamped to the viewport. Any scroll
                 * closes it (anchor would drift). */
                const rect = btn.getBoundingClientRect();
                menu.hidden = false;
                const menuRect = menu.getBoundingClientRect();
                const left = Math.max(8, Math.min(rect.left, window.innerWidth - menuRect.width - 8));
                const bottom = window.innerHeight - rect.top + 6;
                menu.style.left = `${left}px`;
                menu.style.bottom = `${bottom}px`;
                /* base.css gives .modal .modal__wrapper transform:
                 * translateZ(0), which makes the drawer (not the viewport)
                 * the containing block for position: fixed. On desktop the
                 * drawer sits on the right, so the menu opened off-screen
                 * and "+ Add to cart" looked dead (Peter 2026-10-22; phones
                 * were fine, the drawer is full width at 0,0 there). Shift
                 * by however far it actually landed from the anchor. */
                const placed = menu.getBoundingClientRect();
                menu.style.left = `${left - (placed.left - left)}px`;
                menu.style.bottom = `${bottom + (placed.bottom - (rect.top - 6))}px`;
            } else {
                menu.hidden = true;
            }
        }));
        trigger.replaceWith(wrap);
    }
    /* Error line under a picker button, styled like product-form.js errors
     * (.errors.qty-error); role="alert" so screen readers announce it. Pass
     * null to clear. */
    #showPickerError(wrap, message) {
        const existing = wrap.parentElement && wrap.parentElement.querySelector(".hys-mini-picker__error");
        if (existing) existing.remove();
        if (!message) return;
        const error = document.createElement("div");
        error.className = "errors qty-error u-small hys-mini-picker__error";
        error.setAttribute("role", "alert");
        error.textContent = message;
        wrap.before(error);
    }
    /* Hyspecs (2026-10-22, Peter): the tile pictures product.featured_image,
     * which is one colour. When that colour can't be added (sold out, or only
     * ANSI-Only left: ICON Polarized showed Butter Rum), picture the first
     * colour the picker offers instead, and point the tile link at that
     * variant so the PDP opens on it. Returns the variant now shown, or null
     * when the tile is left alone (pictured colour addable, or the image
     * isn't tied to a colour). */
    #showPickerColour(card, eligible) {
        const tileFile = hysImageFile(card.product.featured_image);
        if (!tileFile) return null;
        const pictured = (card.product.variants || []).filter((v => hysImageFile(v.featured_image) === tileFile));
        if (!pictured.length || pictured.some((v => eligible.includes(v)))) return null;
        const shown = eligible.find((v => v.featured_image && v.featured_image.width && v.featured_image.height));
        if (!shown) return null;
        const img = shown.featured_image;
        card.updateImage(img.src, img.width, img.height, img.alt || card.product.title);
        card.querySelectorAll("a[product-card-link]").forEach((link => {
            const url = new URL(link.getAttribute("href"), window.location.origin);
            url.searchParams.set("variant", shown.id);
            link.setAttribute("href", `${url.pathname}${url.search}`);
        }));
        return shown;
    }
    /* The tile renders product.featured_image; a variant only contradicts
     * it when it carries its OWN featured_image with a different CDN file.
     * Compare basenames — the recommendations API and product.js payloads
     * disagree on protocol/prefix ("//cdn.shopify.com/..." vs
     * "/cdn/shop/files/...") but the uploaded filename is unique either
     * way. No variant image = nothing contradicting the tile = shortcut
     * stays (adding it shows the product image in the cart anyway). */
    #variantMatchesTileImage(variant, product) {
        const variantFile = hysImageFile(variant.featured_image);
        const tileFile = hysImageFile(product.featured_image);
        return !variantFile || !tileFile || variantFile === tileFile;
    }
    /* Fetch product.js for pool items that ranked into the carousel, then
     * re-render. Each handle is fetched once per page; a failure is stored as
     * null so it is skipped rather than retried on every cart change. */
    async #loadPoolProducts(handles) {
        const todo = handles.filter((h => !this.poolLoading.has(h)));
        if (!todo.length) return;
        todo.forEach((h => this.poolLoading.add(h)));
        const rootRoute = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : "/";
        await Promise.all(todo.map((async handle => {
            const entry = this.pool.find((p => p.handle === handle));
            try {
                const response = await fetch(`${rootRoute}products/${handle}.js`);
                this.poolProducts.set(entry.id, response.ok ? await response.json() : null);
            } catch (e) {
                this.poolProducts.set(entry.id, null);
            }
        })));
        if (this.rerender) this.rerender();
    }
    /* Same minimum the free-shipping bar compares against (cents, per
     * market); 0 when the bar is off, which skips the shipping score. */
    #freeShippingMinimum() {
        const bar = document.querySelector("free-shipping-bar[minimum-value]");
        return bar ? Number(bar.getAttribute("minimum-value")) || 0 : 0;
    }
    disconnectedCallback() {
        /* Bumping the connection token makes any in-flight pinned/pool
         * fetch from this connection a no-op when it resolves. */
        this.hysConnection = (this.hysConnection || 0) + 1;
        if (this.unsubscribe) this.unsubscribe();
        this.unsubscribe = null;
        if (this.hysOnDocScroll) document.removeEventListener("scroll", this.hysOnDocScroll, {
            capture: true
        });
        if (this.hysOnDocClick) document.removeEventListener("click", this.hysOnDocClick);
        if (this.hysOnCartLoading) document.removeEventListener("on:cart:loading", this.hysOnCartLoading);
        if (this.hysOnCartDone) {
            document.removeEventListener("on:cart:loaded", this.hysOnCartDone);
            document.removeEventListener("on:cart:failed", this.hysOnCartDone);
        }
        if (this.hysOnSubmit) this.removeEventListener("submit", this.hysOnSubmit, true);
    }
    #createCard() {
        const cardTemplate = document.getElementById(`${this.sectionId}--template-cart-recommendations-card`).content;
        const cardFragment = cardTemplate.cloneNode(true);
        const card = cardFragment.children[0];
        return card;
    }
}

customElements.define("cart-recommendations", CartRecommendations);
//# sourceMappingURL=cart-recommendations.js.map
